/**
 * /store/vendor-setup — Vendor onboarding hub.
 * Authenticated. Shows only if the user has a store_vendors row.
 * Step 1: Choose community app.  Step 2: Launch Stripe Connect onboarding.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useProfile } from "@/lib/profile/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { APPS, type AppId } from "@/lib/apps";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import StoreSubnav from "@/components/store/StoreSubnav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, ExternalLink, Sparkles, Store } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type VendorRow = {
  id: string;
  display_name: string;
  app_id: string | null;
  status: string;
  stripe_account_id: string | null;
  stripe_charges_enabled: boolean;
  stripe_details_submitted: boolean;
  metadata: Record<string, unknown> | null;
};

const StoreVendorSetup = () => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { toast } = useToast();
  const [vendor, setVendor] = useState<VendorRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedApp, setSelectedApp] = useState<AppId | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [sandboxLoading, setSandboxLoading] = useState(false);

  const isPartner = profile?.kyc_provider === "partner_code";
  const isSandboxVendor = !!(vendor?.metadata as Record<string, unknown> | null)?.sandbox;

  // Fetch vendor row
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("store_vendors")
        .select("id, display_name, app_id, status, stripe_account_id, stripe_charges_enabled, stripe_details_submitted, metadata")
        .eq("user_id", user.id)
        .maybeSingle();
      setVendor(data as VendorRow | null);
      if (data?.app_id) setSelectedApp(data.app_id as AppId);
      setLoading(false);
    })();
  }, [user]);

  const saveAppChoice = async () => {
    if (!vendor || !selectedApp) return;
    setSaving(true);
    const { error } = await supabase
      .from("store_vendors")
      .update({ app_id: selectedApp })
      .eq("id", vendor.id);
    setSaving(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setVendor((v) => v ? { ...v, app_id: selectedApp } : v);
      toast({ title: "Community saved", description: `Your shop is now affiliated with ${APPS.find((a) => a.id === selectedApp)?.name ?? selectedApp}.` });
    }
  };

  const activateSandboxVendor = async () => {
    setSandboxLoading(true);
    const { data, error } = await supabase.functions.invoke("store-vendor-sandbox-activate", {
      body: {},
    });
    setSandboxLoading(false);
    if (error || !data?.vendor) {
      toast({ title: "Error", description: error?.message ?? "Could not activate sandbox vendor.", variant: "destructive" });
      return;
    }
    setVendor(data.vendor as VendorRow);
    if (data.vendor.app_id) setSelectedApp(data.vendor.app_id as AppId);
    toast({
      title: "Sandbox vendor activated",
      description: "You can explore the full vendor portal — Stripe is bypassed.",
    });
  };

  const launchStripeOnboarding = async () => {
    if (!vendor) return;
    setStripeLoading(true);
    const origin = window.location.origin;
    const { data, error } = await supabase.functions.invoke("store-vendor-onboard", {
      body: {
        return_url: `${origin}/store/vendor-setup?stripe=done`,
        refresh_url: `${origin}/store/vendor-setup?stripe=refresh`,
      },
    });
    setStripeLoading(false);
    if (error || !data?.url) {
      toast({ title: "Error", description: error?.message ?? "Could not start onboarding.", variant: "destructive" });
      return;
    }
    window.location.href = data.url;
  };


  if (!user) {
    return (
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <SiteHeader />
        <StoreSubnav />
        <main className="flex-1 flex items-center justify-center">
          <Card className="p-12 text-center max-w-md">
            <h1 className="text-xl font-semibold">Sign in to continue</h1>
            <p className="text-sm text-muted-foreground mt-2">You need to be signed in to set up your vendor account.</p>
          </Card>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <StoreSubnav />
      <main className="flex-1 mx-auto w-full max-w-3xl px-6 py-10 space-y-8">
        <div>
          <div className="flex items-center gap-3 text-primary">
            <Store className="h-5 w-5" />
            <span className="text-xs uppercase tracking-widest font-semibold">Vendor setup</span>
          </div>
          <h1 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">Set up your shop</h1>
          <p className="mt-2 text-muted-foreground max-w-xl">Choose your community and connect your payment account to start selling on the Wavlynq Store.</p>
        </div>

        {isSandboxVendor && (
          <Card className="p-4 border-primary/40 bg-primary/5 flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-primary shrink-0" />
            <div className="flex-1 text-sm">
              <span className="font-medium">Sandbox vendor mode</span>
              <span className="text-muted-foreground"> — explore the full portal. Stripe is bypassed and no real payouts will occur.</span>
            </div>
            <Badge variant="outline" className="border-primary/40 text-primary">SANDBOX</Badge>
          </Card>
        )}

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-32 rounded-lg" />
            <Skeleton className="h-32 rounded-lg" />
          </div>
        ) : !vendor ? (
          isPartner ? (
            <Card className="p-8 text-center space-y-4">
              <div className="flex justify-center">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
              </div>
              <div>
                <h2 className="text-lg font-semibold">Activate your sandbox vendor</h2>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                  As a partner you can experience the full vendor portal without going through Stripe Connect.
                  We'll provision a sandbox shop you can use to list products, manage orders, and explore the dashboard.
                </p>
              </div>
              <Button onClick={activateSandboxVendor} disabled={sandboxLoading} size="lg">
                {sandboxLoading ? "Activating…" : "Activate sandbox vendor"}
              </Button>
            </Card>
          ) : (
            <Card className="p-12 text-center">
              <h2 className="text-lg font-semibold">No vendor account found</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Apply as a vendor through the{" "}
                <a href="/partner-access#apply" className="underline">Partner access</a>{" "}
                page first.
              </p>
            </Card>
          )
        ) : (
          <>
            {/* Step 1 — Community */}
            <Card className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-lg">1 · Choose your community</h2>
                {vendor.app_id && (
                  <span className="flex items-center gap-1 text-sm text-green-500">
                    <CheckCircle className="h-4 w-4" /> Saved
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Which community does your brand belong to? Your products will appear when members filter by this app.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {APPS.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setSelectedApp(a.id)}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors text-left ${
                      selectedApp === a.id
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                    }`}
                  >
                    <img src={a.icon} alt="" className="h-5 w-5 rounded-sm shrink-0" />
                    {a.name}
                  </button>
                ))}
              </div>
              <Button
                onClick={saveAppChoice}
                disabled={!selectedApp || saving || selectedApp === vendor.app_id}
                size="sm"
              >
                {saving ? "Saving…" : vendor.app_id ? "Update community" : "Save community"}
              </Button>
            </Card>

            {/* Step 2 — Stripe Connect (skipped for sandbox vendors) */}
            <Card className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-lg">2 · Connect payments</h2>
                {(vendor.stripe_charges_enabled || isSandboxVendor) && (
                  <span className="flex items-center gap-1 text-sm text-green-500">
                    <CheckCircle className="h-4 w-4" /> {isSandboxVendor ? "Sandbox" : "Connected"}
                  </span>
                )}
              </div>
              {isSandboxVendor ? (
                <p className="text-sm text-foreground">
                  Stripe Connect is bypassed in sandbox mode. You can list products and process simulated orders without entering real banking details.
                </p>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    We use Stripe to handle payments securely. You'll be redirected to complete your account setup.
                  </p>
                  {vendor.stripe_charges_enabled ? (
                    <p className="text-sm text-foreground">
                      Your payment account is active and ready to receive payouts. 🎉
                    </p>
                  ) : (
                    <Button onClick={launchStripeOnboarding} disabled={stripeLoading || !vendor.app_id}>
                      {stripeLoading ? "Redirecting…" : vendor.stripe_account_id ? "Continue Stripe setup" : "Connect with Stripe"}
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                  {!vendor.app_id && (
                    <p className="text-xs text-muted-foreground">Choose a community first to unlock this step.</p>
                  )}
                </>
              )}
            </Card>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default StoreVendorSetup;
